'use client';
import { useContext } from 'react';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';
import { LangCurrencyContext, type LangCurrencyContextType } from '@/contexts/LangCurrencyContext';

/**
 * Public engineering page — exposes the infrastructure, security posture,
 * quality gates, product lifecycle and operations practice that normally live
 * in the repository's docs/ folder. Everything here is also verifiable: the
 * health endpoint, the audit scripts and the CI pipeline are listed with their
 * real numbers.
 *
 * Copy is stored as `{ en, th }` pairs and resolved from the active locale,
 * so the page cannot ship half-English and the translation-coverage gate
 * verifies the Thai side structurally rather than by dictionary lookup.
 */

type Bilingual = { en: string; th: string };

/** Every user-visible string on this page is written as a pair, never alone. */
const b = (en: string, th: string): Bilingual => ({ en, th });

const COPY = {
  title: b('Engineering at Glanz CRM', 'งานวิศวกรรมของ Glanz CRM'),
  intro: b(
    'How the platform is built, secured, tested and operated. Every claim on this page is backed by an automated check that runs on every release.',
    'แพลตฟอร์มนี้ถูกสร้าง รักษาความปลอดภัย ทดสอบ และดูแลการทำงานอย่างไร ทุกข้อความบนหน้านี้มีการตรวจสอบอัตโนมัติรองรับในทุกรุ่น'
  ),
  stack: b('Technology stack', 'เทคโนโลยีที่ใช้'),
  gates: b('Quality gates', 'เกณฑ์คุณภาพ'),
  gatesIntro: b(
    'Every pull request passes all of these before it can ship.',
    'ทุก pull request ต้องผ่านการตรวจสอบทั้งหมดเหล่านี้ก่อนจึงจะออกได้'
  ),
  security: b('Security posture', 'สถานะความปลอดภัย'),
  securityLead: b('Full details are published on the', 'รายละเอียดทั้งหมดเผยแพร่บน'),
  securityLink: b('security page', 'หน้าความปลอดภัย'),
  pipeline: b('Delivery pipeline', 'กระบวนการส่งมอบงาน'),
  operations: b('Operations and reliability', 'การดำเนินงานและความน่าเชื่อถือ'),
  accessibility: b('Accessibility', 'การเข้าถึง'),
  lifecycle: b('Product lifecycle', 'วงจรชีวิตผลิตภัณฑ์'),
  lifecycleIntro: b(
    'Customers should never have to guess where they stand. Trial, paid and cancelled states are decided by the server, never by a value cached in the browser.',
    'ลูกค้าไม่ควรต้องเดาว่าตัวเองอยู่ในสถานะใด สถานะการทดลองใช้ แพ็กเกจที่ชำระแล้ว และการยกเลิก ถูกตัดสินโดยเซิร์ฟเวอร์เสมอ ไม่ใช่ค่าที่แคชไว้ในเบราว์เซอร์'
  ),
  releases: b('Release discipline', 'ระเบียบวินัยการออกรุ่น'),
  releasesIntro: b(
    'Every version we ship is dated, documented and reversible. If a release causes harm, it can be switched off without waiting for the next deploy.',
    'ทุกรุ่นที่เราออกมีวันที่ มีเอกสาร และย้อนกลับได้ หากรุ่นใดก่อให้เกิดปัญหา เราปิดมันได้โดยไม่ต้องรอการอัปเดตครั้งถัดไป'
  ),
  releasesLead: b('Read every release note at', 'อ่านบันทึกการเปลี่ยนแปลงทั้งหมดได้ที่'),
  ctaTitle: b('Questions about our engineering?', 'มีคำถามเกี่ยวกับงานวิศวกรรมของเรา?'),
  ctaBody: b(
    'We are happy to walk through the architecture, the security model or a technical evaluation for your team.',
    'เรายินดีอธิบายสถาปัตยกรรม โมเดลความปลอดภัย หรือจัดการประเมินทางเทคนิคสำหรับทีมของคุณ'
  ),
  ctaButton: b('Talk to us', 'พูดคุยกับเรา'),
} satisfies Record<string, Bilingual>;

const STACK: { label: Bilingual; value: Bilingual }[] = [
  {
    label: { en: 'Application', th: 'แอปพลิเคชัน' },
    value: {
      en: 'Next.js 15 App Router, React 19, TypeScript',
      th: 'Next.js 15 App Router, React 19, TypeScript',
    },
  },
  {
    label: { en: 'Database', th: 'ฐานข้อมูล' },
    value: {
      en: 'Supabase PostgreSQL with 28 migrations and 82 RLS policies',
      th: 'Supabase PostgreSQL พร้อม 28 migration และ 82 นโยบาย RLS',
    },
  },
  {
    label: { en: 'Authentication', th: 'การยืนยันตัวตน' },
    value: {
      en: 'Supabase Auth with server-side sessions and HttpOnly cookies',
      th: 'Supabase Auth พร้อม session ฝั่งเซิร์ฟเวอร์และคุกกี้แบบ HttpOnly',
    },
  },
  {
    label: { en: 'Tenancy', th: 'การแยกข้อมูลผู้ใช้' },
    value: {
      en: 'Row-level security per organisation, enforced in the database',
      th: 'Row-level security แยกตามองค์กร บังคับใช้ในฐานข้อมูล',
    },
  },
  {
    label: { en: 'Interface', th: 'ส่วนติดต่อผู้ใช้' },
    value: {
      en: 'Tailwind CSS, custom engine with full Thai and English parity',
      th: 'Tailwind CSS พร้อมระบบแปลภาษาไทยและอังกฤษครบถ้วน',
    },
  },
  {
    label: { en: 'Documents', th: 'เอกสาร' },
    value: {
      en: 'PDF generation for invoices, quotations, appraisals and memos',
      th: 'สร้าง PDF สำหรับใบแจ้งหนี้ ใบเสนอราคา รายงานประเมิน และเมโม',
    },
  },
  {
    label: { en: 'Email', th: 'อีเมล' },
    value: {
      en: 'Resend transactional mail plus a per-user mailbox',
      th: 'อีเมลธุรกรรมผ่าน Resend พร้อมกล่องจดหมายรายผู้ใช้',
    },
  },
  {
    label: { en: 'Hosting', th: 'โฮสติ้ง' },
    value: {
      en: 'Hostinger Node.js web app behind the LiteSpeed edge',
      th: 'เว็บแอป Node.js บน Hostinger หลัง LiteSpeed edge',
    },
  },
];

const GATES: { label: Bilingual; value: Bilingual }[] = [
  {
    label: { en: 'Unit tests', th: 'การทดสอบยูนิต' },
    value: {
      en: '59 automated tests run by the Node.js test runner',
      th: 'การทดสอบอัตโนมัติ 59 รายการ รันด้วย test runner ของ Node.js',
    },
  },
  {
    label: { en: 'HTTP smoke tests', th: 'การทดสอบผ่าน HTTP' },
    value: {
      en: '13 checks per release across every public page',
      th: '13 รายการตรวจสอบในทุกรุ่น ครอบคลุมทุกหน้าสาธารณะ',
    },
  },
  {
    label: { en: 'SEO audit', th: 'การตรวจสอบ SEO' },
    value: {
      en: '300+ checks covering metadata, structured data and crawl files',
      th: 'มากกว่า 300 รายการตรวจสอบ metadata, structured data และไฟล์สำหรับ crawler',
    },
  },
  {
    label: { en: 'Accessibility audit', th: 'การตรวจสอบการเข้าถึง' },
    value: {
      en: 'Static checks plus a headless-browser sweep with zero violations',
      th: 'ตรวจสอบแบบคงที่ร่วมกับการสแกนด้วยเบราว์เซอร์จริง โดยไม่พบข้อผิดพลาด',
    },
  },
  {
    label: { en: 'Performance audit', th: 'การตรวจสอบประสิทธิภาพ' },
    value: {
      en: '42 checks covering response time, payloads and caching',
      th: '42 รายการตรวจสอบเวลาตอบสนอง ขนาดข้อมูล และแคช',
    },
  },
  {
    label: { en: 'Translation audit', th: 'การตรวจสอบคำแปล' },
    value: {
      en: 'Every interface string and every release note must have a Thai counterpart',
      th: 'ข้อความบนหน้าจอและบันทึกการเปลี่ยนแปลงทุกรายการต้องมีคำแปลภาษาไทย',
    },
  },
];

const SECURITY: { title: Bilingual; body: Bilingual }[] = [
  {
    title: { en: 'Database-level isolation', th: 'แยกข้อมูลระดับฐานข้อมูล' },
    body: {
      en: 'Every tenant table is protected by row-level security, so data cannot leak between organisations.',
      th: 'ทุกตารางของผู้ใช้ได้รับการป้องกันด้วย row-level security ข้อมูลจึงไม่รั่วไหลระหว่างองค์กร',
    },
  },
  {
    title: { en: 'Enforcing content policy', th: 'บังคับใช้นโยบายเนื้อหา' },
    body: {
      en: 'A strict content security policy, HSTS, frame denial and referrer policy are set on every response.',
      th: 'ทุกการตอบกลับมี content security policy แบบเข้มงวด, HSTS, การปฏิเสธการฝังเฟรม และนโยบาย referrer',
    },
  },
  {
    title: { en: 'Role-based access', th: 'สิทธิ์ตามบทบาท' },
    body: {
      en: 'Admin, Manager, Sales and Viewer roles are checked server-side, with plan entitlements resolved centrally.',
      th: 'บทบาท Admin, Manager, Sales และ Viewer ถูกตรวจสอบที่ฝั่งเซิร์ฟเวอร์ และอ่านสิทธิ์ตามแพ็กเกจจากจุดเดียว',
    },
  },
  {
    title: { en: 'Abuse protection', th: 'ป้องกันการละเมิด' },
    body: {
      en: 'Public forms are rate limited per address and per network, validated, and protected with hidden fields.',
      th: 'แบบฟอร์มสาธารณะถูกจำกัดอัตราต่อที่อยู่และเครือข่าย ตรวจสอบข้อมูล และป้องกันด้วยฟิลด์ซ่อน',
    },
  },
  {
    title: { en: 'Accountability', th: 'การตรวจสอบย้อนหลัง' },
    body: {
      en: 'Every user action is recorded with the user, the action, the IP address and the session.',
      th: 'ทุกการกระทำของผู้ใช้ถูกบันทึกพร้อมผู้ใช้ การกระทำ หมายเลข IP และ session',
    },
  },
];

const PIPELINE: Bilingual[] = [
  { en: 'Install dependencies and cache them', th: 'ติดตั้งและแคช dependency' },
  {
    en: 'Lint the whole codebase, zero errors allowed',
    th: 'ตรวจโค้ดทั้งหมด ไม่อนุญาตให้มีข้อผิดพลาดแม้แต่ข้อเดียว',
  },
  { en: 'Type-check every TypeScript file', th: 'ตรวจชนิดข้อมูลทุกไฟล์ TypeScript' },
  { en: 'Run the unit test suite', th: 'รันชุดทดสอบยูนิต' },
  {
    en: 'Verify Thai and English translation coverage',
    th: 'ตรวจความครอบคลุมของคำแปลไทยและอังกฤษ',
  },
  { en: 'Build the production application', th: 'บิลด์แอปพลิเคชันสำหรับใช้งานจริง' },
  {
    en: 'Start the production server and run the smoke suite',
    th: 'เปิดเซิร์ฟเวอร์จริงและรันชุดทดสอบ smoke',
  },
  {
    en: 'Run SEO, accessibility and performance audits against it',
    th: 'รันการตรวจสอบ SEO การเข้าถึง และประสิทธิภาพกับเซิร์ฟเวอร์นั้น',
  },
];

const OPERATIONS: { title: Bilingual; body: Bilingual }[] = [
  {
    title: { en: 'Health endpoint', th: 'Health endpoint' },
    body: {
      en: 'A public endpoint reports application and database status in real time.',
      th: 'endpoint สาธารณะรายงานสถานะของแอปพลิเคชันและฐานข้อมูลแบบเรียลไทม์',
    },
  },
  {
    title: { en: 'Uptime monitoring', th: 'การเฝ้าระวังสถานะ' },
    body: {
      en: 'The health endpoint is polled every minute with alerting on sustained failure.',
      th: 'ตรวจสอบ health endpoint ทุกนาที พร้อมแจ้งเตือนเมื่อล้มเหลวต่อเนื่อง',
    },
  },
  {
    title: { en: 'Backups', th: 'การสำรองข้อมูล' },
    body: {
      en: 'Daily point-in-time database backups with documented retention.',
      th: 'สำรองฐานข้อมูลแบบ point-in-time ทุกวัน พร้อมกำหนดระยะเวลาเก็บรักษาที่เป็นเอกสาร',
    },
  },
  {
    title: { en: 'Rollback', th: 'การย้อนกลับ' },
    body: {
      en: 'Every release keeps the previous build ready for one-step restoration.',
      th: 'ทุกรุ่นเก็บบิลด์ก่อนหน้าไว้ พร้อมคืนสถานะได้ในขั้นตอนเดียว',
    },
  },
  {
    title: { en: 'Incident runbook', th: 'คู่มือรับเหตุ' },
    body: {
      en: 'A documented first-response path from health check to mitigation.',
      th: 'คู่มือการตอบสนองเหตุเบื้องต้นตั้งแต่การตรวจสุขภาพระบบจนถึงการแก้ไข',
    },
  },
  {
    title: { en: 'Error reporting', th: 'การรายงานข้อผิดพลาด' },
    body: {
      en: 'Client errors are reported to a rate-limited ingest endpoint, stripped of user data, and written to the application log so a failure leaves evidence behind.',
      th: 'ข้อผิดพลาดจากฝั่งผู้ใช้ถูกส่งไปยัง endpoint ที่จำกัดอัตรา ตัดข้อมูลผู้ใช้ออก และบันทึกลง log ของระบบ เพื่อให้เหตุการณ์ที่เกิดขึ้นมีหลักฐาน',
    },
  },
];

const LIFECYCLE: { title: Bilingual; body: Bilingual }[] = [
  {
    title: { en: 'Trial you can trust', th: 'การทดลองใช้ที่เชื่อถือได้' },
    body: {
      en: 'Trial start and expiry are decided by the server. The browser cache is only an offline fallback, so changing device or clock cannot extend a trial.',
      th: 'การเริ่มและสิ้นสุดการทดลองใช้ถูกตัดสินโดยเซิร์ฟเวอร์ แคชในเบราว์เซอร์เป็นเพียงข้อมูลสำรองเมื่อออฟไลน์ การเปลี่ยนอุปกรณ์หรือนาฬิกาจึงต่ออายุการทดลองไม่ได้',
    },
  },
  {
    title: { en: 'Nothing is lost on expiry', th: 'ไม่มีข้อมูลสูญหายเมื่อหมดอายุ' },
    body: {
      en: 'When a trial ends the account becomes read-only rather than deleted. Data is kept so an upgrade restores everything exactly as it was.',
      th: 'เมื่อการทดลองใช้สิ้นสุด บัญชีจะเปลี่ยนเป็นแบบอ่านอย่างเดียวแทนที่จะถูกลบ ข้อมูลยังคงอยู่เพื่อให้อัปเกรดแล้วกลับมาได้ครบถ้วน',
    },
  },
  {
    title: { en: 'Automated trial lifecycle', th: 'วงจรการทดลองใช้อัตโนมัติ' },
    body: {
      en: 'Scheduled jobs process expiries and send trial-ending, expiry and win-back email, de-duplicated so a customer is never emailed twice for the same event.',
      th: 'งานตามเวลาจะประมวลผลการหมดอายุและส่งอีเมลแจ้งใกล้หมดอายุ หมดอายุ และดึงลูกค้ากลับมาใช้บริการ โดยตัดรายการซ้ำเพื่อไม่ให้ลูกค้าได้รับอีเมลซ้ำ',
    },
  },
  {
    title: { en: 'Cancellation you control', th: 'การยกเลิกที่คุณควบคุมได้' },
    body: {
      en: 'Cancel from settings, keep access until the period ends, and receive a confirmation describing exactly what happens and when.',
      th: 'ยกเลิกได้จากหน้าการตั้งค่า ใช้งานต่อได้จนถึงวันสิ้นสุดของรอบ และได้รับอีเมลยืนยันที่ระบุชัดเจนว่าจะเกิดอะไรขึ้นและเมื่อใด',
    },
  },
  {
    title: { en: 'A published deprecation policy', th: 'นโยบายการยกเลิกฟีเจอร์ที่ประกาศไว้' },
    body: {
      en: 'Anything removed or changed incompatibly is announced at least 30 days ahead in the product changelog before it happens.',
      th: 'สิ่งที่จะถูกนำออกหรือเปลี่ยนในรูปแบบที่ไม่เข้ากันได้ จะถูกประกาศล่วงหน้าอย่างน้อย 30 วันในบันทึกการเปลี่ยนแปลงก่อนดำเนินการ',
    },
  },
];

const RELEASES: { title: Bilingual; body: Bilingual }[] = [
  {
    title: { en: 'Semantic versions', th: 'เลขเวอร์ชันตามมาตรฐาน' },
    body: {
      en: 'Major for breaking changes, minor for features, patch for fixes. Versions are never reused or renumbered.',
      th: 'เลขใหญ่สำหรับการเปลี่ยนที่พักภูมิ เลขกลางสำหรับฟีเจอร์ใหม่ เลขย่อยสำหรับการแก้ไขบั๊ก และไม่มีการใช้เลขเวอร์ชันซ้ำ',
    },
  },
  {
    title: { en: 'A public changelog', th: 'บันทึกการเปลี่ยนแปลงสาธารณะ' },
    body: {
      en: 'Every release is dated and described on a public changelog, with an RSS feed for customers who want to follow along.',
      th: 'ทุกรุ่นถูกบันทึกวันที่และรายละเอียดไว้ในบันทึกการเปลี่ยนแปลงสาธารณะ พร้อมฟีด RSS สำหรับลูกค้าที่ต้องการติดตาม',
    },
  },
  {
    title: { en: 'Bilingual release notes', th: 'บันทึกการเปลี่ยนแปลงสองภาษา' },
    body: {
      en: 'Notes ship in Thai and English together, and a missing Thai string fails the build rather than shipping half a translation.',
      th: 'บันทึกการเปลี่ยนแปลงถูกส่งมอบพร้อมกันทั้งภาษาไทยและอังกฤษ และหากข้อความภาษาไทยขาดหาย ระบบจะไม่ผ่านการตรวจสอบ',
    },
  },
  {
    title: { en: 'Kill switches, not hotfix panic', th: 'สวิตช์ปิดฉุกเฉิน ไม่ใช่การแก้ด่วน' },
    body: {
      en: 'Every optional surface sits behind a feature flag with a documented owner and default, so a bad release is switched off by configuration rather than by an emergency patch.',
      th: 'ทุกส่วนที่เปิดปิดได้อยู่หลังธงฟีเจอร์ที่มีผู้รับผิดชอบและค่าเริ่มต้นระบุไว้ จึงปิดรุ่นที่มีปัญหาได้ด้วยการตั้งค่า ไม่ต้องแก้โค้ดฉุกเฉิน',
    },
  },
  {
    title: { en: 'Automatic quality gates', th: 'เกณฑ์คุณภาพอัตโนมัติ' },
    body: {
      en: 'A release that fails lint, types, tests, translation coverage or any audit cannot be promoted, because the pipeline fails first.',
      th: 'รุ่นที่ไม่ผ่านการตรวจโค้ด ชนิดข้อมูล การทดสอบ ความครอบคลุมของคำแปล หรือการตรวจสอบใด ๆ จะไม่ถูกนำขึ้นใช้งาน เพราะกระบวนการจะหยุดก่อน',
    },
  },
];

const ACCESSIBILITY: Bilingual[] = [
  {
    en: 'Conforms to WCAG 2.1 Level AA across the whole public site',
    th: 'ผ่านมาตรฐาน WCAG 2.1 ระดับ AA ทั้งเว็บไซต์สาธารณะ',
  },
  {
    en: 'Keyboard bypass link as the first focusable element on every page',
    th: 'ลิงก์ข้ามไปยังเนื้อหาด้วยคีย์บอร์ด เป็นองค์ประกอบที่โฟกัสได้ลำดับแรกในทุกหน้า',
  },
  {
    en: 'Visible focus indicators and full reduced-motion support',
    th: 'มีตัวบอกตำแหน่งโฟกัสที่มองเห็นได้ และรองรับการลดการเคลื่อนไหวอย่างเต็มที่',
  },
  {
    en: 'Every form control programmatically labelled',
    th: 'ทุกองค์ประกอบของแบบฟอร์มมีป้ายกำกับในระดับโปรแกรม',
  },
  {
    en: 'Verified with an automated audit on every release',
    th: 'ตรวจสอบด้วยการตรวจสอบอัตโนมัติในทุกรุ่น',
  },
];

export default function EngineeringPage() {
  const ctx = useContext(LangCurrencyContext as React.Context<LangCurrencyContextType | null>);
  const lang = ctx?.lang === 'TH' ? 'TH' : 'EN';
  const t = (s: Bilingual) => (lang === 'TH' ? s.th : s.en);
  // Flatten the pair map to the active locale once, so the markup below reads
  // as plain strings instead of threading `t()` through every attribute.
  const copy = Object.fromEntries(
    Object.entries(COPY).map(([key, value]) => [key, t(value)])
  ) as Record<keyof typeof COPY, string>;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]">
      <MarketingNav />
      <main id="main-content" className="pt-28 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-14">
          <header>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              {copy.title}
            </h1>
            <p className="text-lg text-[#9ca3af] leading-relaxed">{copy.intro}</p>
          </header>

          <section>
            <h2 className="text-2xl font-bold text-white mb-6">{copy.stack}</h2>
            <dl className="grid sm:grid-cols-2 gap-4">
              {STACK.map((s) => (
                <div
                  key={t(s.label)}
                  className="rounded-xl border border-white/10 bg-[#0f1524] p-4"
                >
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#9ca3af]">
                    {t(s.label)}
                  </dt>
                  <dd className="text-sm text-white mt-1.5 leading-relaxed">{t(s.value)}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{copy.gates}</h2>
            <p className="text-[#9ca3af] mb-6">{copy.gatesIntro}</p>
            <ul className="space-y-3">
              {GATES.map((g) => (
                <li
                  key={t(g.label)}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 rounded-lg border border-white/5 bg-[#0f1524] px-4 py-3"
                >
                  <span className="text-sm font-semibold text-white sm:w-56 sm:shrink-0">
                    {t(g.label)}
                  </span>
                  <span className="text-sm text-[#9ca3af]">{t(g.value)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-6">{copy.security}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {SECURITY.map((s) => (
                <div
                  key={t(s.title)}
                  className="rounded-xl border border-white/10 bg-[#0f1524] p-5"
                >
                  <h3 className="text-base font-bold text-white mb-2">{t(s.title)}</h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">{t(s.body)}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#9ca3af] mt-4">
              {copy.securityLead}{' '}
              <a href="/security" className="text-violet-400 underline">
                {copy.securityLink}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-6">{copy.pipeline}</h2>
            <ol className="space-y-2.5">
              {PIPELINE.map((step, i) => (
                <li key={t(step)} className="flex gap-3 text-sm text-[#9ca3af]">
                  <span className="text-violet-400 font-mono shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>{t(step)}</span>
                </li>
              ))}
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{copy.lifecycle}</h2>
            <p className="text-[#9ca3af] mb-6">{copy.lifecycleIntro}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {LIFECYCLE.map((o) => (
                <div
                  key={t(o.title)}
                  className="rounded-xl border border-white/10 bg-[#0f1524] p-5"
                >
                  <h3 className="text-base font-bold text-white mb-2">{t(o.title)}</h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">{t(o.body)}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{copy.releases}</h2>
            <p className="text-[#9ca3af] mb-6">{copy.releasesIntro}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {RELEASES.map((o) => (
                <div
                  key={t(o.title)}
                  className="rounded-xl border border-white/10 bg-[#0f1524] p-5"
                >
                  <h3 className="text-base font-bold text-white mb-2">{t(o.title)}</h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">{t(o.body)}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#9ca3af] mt-4">
              {copy.releasesLead}{' '}
              <a href="/changelog" className="text-violet-400 underline">
                /changelog
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-6">{copy.operations}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {OPERATIONS.map((o) => (
                <div
                  key={t(o.title)}
                  className="rounded-xl border border-white/10 bg-[#0f1524] p-5"
                >
                  <h3 className="text-base font-bold text-white mb-2">{t(o.title)}</h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">{t(o.body)}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">{copy.accessibility}</h2>
            <ul className="space-y-2.5">
              {ACCESSIBILITY.map((a) => (
                <li key={t(a)} className="flex gap-3 text-sm text-[#9ca3af]">
                  <span className="text-emerald-400 shrink-0" aria-hidden="true">
                    ✓
                  </span>
                  <span>{t(a)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-violet-500/30 bg-violet-500/10 p-6">
            <h2 className="text-xl font-bold text-white mb-2">{copy.ctaTitle}</h2>
            <p className="text-sm text-[#9ca3af] mb-4">{copy.ctaBody}</p>
            <a
              href="/contact"
              className="inline-block px-5 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-colors"
            >
              {copy.ctaButton}
            </a>
          </section>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
